import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import type { Product } from "@/api";
import { FavoriteHeartIcon, HeartFilledIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { type AdSlot, trackAdClick, useAdImpressionRef } from "@/lib/ad-tracking";
import { useImpressionRef } from "@/lib/discovery";
import { type ProductTile, productTiles } from "@/lib/product-tiles";
import { rememberDiscoverySource, useDiscoverySource } from "@/lib/discovery-source";
import { cn } from "@/lib/utils";
import { useCart, useWishlist } from "@/store";
import { Price } from "./price";
import { notifyAddedToCart } from "@/lib/cart-toast";
import { useDoubleTapLike } from "@/lib/use-double-tap-like";
import { orderQty } from "@/lib/min-order";
import { visibleBadges } from "@/lib/product-badges";

// Карточка товара 1:1 по Figma-кадру «Главная+Каталог» (card 270×428): фото r4 без
// тени, лайк — «голое» сердце 20×18 (белая заливка 50 % + чёрная обводка 50 % 0,5px)
// в зоне 48×48 у правого верхнего угла, без белого кружка-подложки; в избранном
// сердце заливается tangelo. Под фото через 8px — цена, через 8px бренд (#2E2E2E 16)
// и название (#ABABAB 14) с зазором 3px. В покое кнопки нет — как в макете;
// «В корзину» (r8, как button 154×32 макета) проявляется поверх низа фото при
// наведении/фокусе. Клик по карточке ведёт на страницу товара.
//
// Карточка — один вариант-цвет (A-09, Айталы 02.10): фото, цена (ровно этого
// варианта, без «от») и ссылка `?variant=` берутся из плитки `tile`. Без плитки
// карточка рисует первую плитку товара.
//
// `variant="rail"` — карточка подборки на главной (кадр 2871:30392, «Название
// подборки»): 173×319, фото 173×229, одна строка названия 400/14 и серая кнопка
// «В корзину» 32px r8 под ценой — в покое, а не на наведении.
//
// `ad` — рекламная позиция (этап 8, П1): метка «Реклама» на фото, показ и
// клик уходят в рекламную статистику кампании (lib/ad-tracking).
export function ProductCard({
  product,
  tile: tileProp,
  variant = "grid",
  ad
}: {
  product: Product;
  tile?: ProductTile;
  variant?: "grid" | "rail";
  ad?: AdSlot;
}) {
  const tile = tileProp ?? productTiles(product)[0];
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const saved = useWishlist(s => s.ids.includes(product.id));
  const toggleProduct = useWishlist(s_0 => s_0.toggleProduct);
  const setProductLikedOnServer = useWishlist(s_1 => s_1.setProductLikedOnServer);
  const addToCart = useCart(s_2 => s_2.add);
  // Показ карточки засчитывается по её появлению в окне просмотра, а не по
  // рендеру: сетка рисует всю страницу выдачи разом, а видит покупатель
  // два-три ряда.
  const discoveryRef = useImpressionRef(product.id);
  const adRef = useAdImpressionRef(ad, product.id);
  const impressionRef = useCallback((element: HTMLDivElement | null) => {
    const offDiscovery = discoveryRef(element);
    const offAd = adRef(element);
    return () => {
      offDiscovery?.();
      offAd?.();
    };
  }, [discoveryRef, adRef]);
  // Откуда покупатель пришёл к товару. Запоминается на переходе в карточку, а
  // не на чекауте: к моменту оплаты путь уже забыт (см. lib/discovery-source).
  const source = useDiscoverySource();
  // Минимальный заказ: «В корзину» кладёт сразу N, остаток меньше N - как нет в наличии.
  const minQty = orderQty(product);
  const soldOut = (tile?.stock ?? product.stock) < minQty;
  const cover = tile?.photo ?? product.photos[0];
  const href = tile?.href ?? `/product/${product.id}`;
  const rail = variant === "rail";
  // Бейджи «Хит», «Новинка», «Распродажа», «Эко» словами на плитке (fd-catalog-20).
  const tileBadges = visibleBadges(product.badges, rail ? 1 : 2);
  // Если у товара несколько размеров/вариантов, «В корзину» из каталога ведёт на
  // страницу товара (там выбирают размер); иначе быстро кладём в корзину.
  const needsChoice = (product.variants?.length ?? 0) > 0 || product.sizes.length > 1;
  const toggleLike = () => {
    const liked = toggleProduct(product.id);
    void setProductLikedOnServer(product.id, liked);
  };
  // Двойной тап по фото — лайк (только ставит, как в мобильном клиенте;
  // снять лайк — сердечком). Одиночный тап открывает товар.
  const photoTap = useDoubleTapLike({
    onOpen: () => {
      rememberDiscoverySource(source);
      navigate(href);
    },
    onLike: () => {
      if (!useWishlist.getState().ids.includes(product.id)) toggleLike();
    }
  });
  const quickAdd = async () => {
    if (soldOut) return;
    rememberDiscoverySource(source);
    if (needsChoice) {
      navigate(href);
      return;
    }
    try {
      await addToCart({
        productId: product.id,
        size: product.sizes[0] ?? "",
        color: tile?.color || product.color,
        qty: minQty
      });
      notifyAddedToCart(t, () => navigate("/cart"), {
        photo: cover,
        brand: product.brand,
        name: product.name
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.error"));
    }
  };
  return (
    // touch-manipulation: без него iOS съедает двойной тап под зум страницы.
    <div className="group flex touch-manipulation flex-col gap-2" ref={impressionRef} data-ad-campaign={ad?.campaignId} onClickCapture={ad ? e_0 => {
      // Клик по рекламе — переход в товар или «В корзину»; сердечко — нет.
      const target = (e_0.target as HTMLElement).closest("a, button");
      if (target && !target.hasAttribute("aria-pressed")) trackAdClick(ad, product.id);
    } : undefined}>
			<div className={cn("relative overflow-hidden rounded-[4px] bg-osmix-gray-100", rail ? "aspect-[173/229]" : "aspect-[270/359]")}>
				<Link to={href} onPointerDown={photoTap.onPointerDown} onClick={e_1 => {
          photoTap.onClick(e_1);
          if (!e_1.defaultPrevented) rememberDiscoverySource(source);
        }} className="absolute inset-0 outline-none focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">
					{cover ? <img src={cover} alt={product.name} loading="lazy" className={cn("size-full object-cover transition-transform duration-300 group-hover:scale-105", soldOut && "opacity-60")} /> : <div className="flex size-full items-center justify-center text-osmix-icon type-small">
							{product.name}
						</div>}
				</Link>

				{photoTap.burst > 0 && <HeartFilledIcon key={photoTap.burst} size={64} className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white drop-shadow-lg animate-out fade-out zoom-out-150 duration-700 fill-mode-forwards" />}

				{/* «Избранное на фото» макета: зона 48×48 (pad 8), сердце 32 — белое 50 %
        с обводкой 50 %, в избранном залито #FA5C2E. */}
				<button type="button" aria-label={saved ? t("wishlist.remove") : t("wishlist.add")} aria-pressed={saved} onClick={toggleLike} className="absolute right-0 top-0 flex size-12 items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">
					<FavoriteHeartIcon active={saved} />
				</button>

				{(soldOut || tileBadges.length > 0) && <div className="pointer-events-none absolute left-2 top-2 flex max-w-[calc(100%-56px)] flex-wrap items-start gap-1">
						{soldOut && <span className="rounded-[6px] bg-osmix-text-1/85 px-2.5 py-1 type-small text-white backdrop-blur">
								{t("product.outOfStock")}
							</span>}
						{tileBadges.map(badge => <span key={badge} data-testid="product-badge" className={cn("rounded-[6px] px-2 py-0.5 text-[12px] font-semibold leading-4 backdrop-blur", badge === "sale" ? "bg-osmix-tangelo text-white" : "bg-white/90 text-osmix-text-1")}>
								{t(`product.badges.${badge}`)}
							</span>)}
					</div>}

				{/* Метка рекламной позиции (П1) — внизу слева, чтобы не спорить с «Нет в наличии». */}
				{ad && <span className="pointer-events-none absolute bottom-2 left-2 rounded-[6px] bg-white/90 px-2 py-0.5 text-[11px] leading-4 text-osmix-text-1 backdrop-blur group-hover:opacity-0" data-testid="ad-label">
						{t("ads.label")}
					</span>}

				{/* «В корзину» проявляется на наведении/фокусе поверх низа фото —
        в покое карточка чистая (как в макете), без сдвига сетки. Плашка
        без иконки корзины, только текст (Айталы 21.10, photo_115). */}
				{!soldOut && !rail && <div className="pointer-events-none absolute inset-x-2 bottom-2 translate-y-2 opacity-0 transition-all duration-200 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100">
						<button type="button" onClick={quickAdd} className="flex h-9 w-full items-center justify-center rounded-[8px] bg-white/95 type-body2-bold text-osmix-text-1 shadow-md backdrop-blur outline-none transition-colors hover:bg-white focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">
							{t("product.addToCart")}
						</button>
					</div>}
			</div>

			{/* цена → бренд → название (порядок из макета; кнопки на hover над фото).
       Отступы — по компонентам Figma PriceContainer и title (Айталы 20.10,
       photo_105/106): цена 16/20, через 2 бренд, через 2 название. Бренд
       16/16 #1F1F1F (строка без воздуха, как 14/14 в title), название
       400/14/18 #ABABAB. Раньше цена отстояла от бренда на 8 + воздух
       строки 22, а бренд от названия на 3 + воздух строки 20. */}
			{rail ? <div className="flex flex-col gap-2 px-1.5">
					{/* В подборке скидочная цена — tangelo с tangelo-бейджем, как в макете. */}
					<Price value={tile?.price ?? product.price} oldValue={tile?.oldPrice} pill={tile?.oldPrice != null && tile.oldPrice > tile.price ? "tangelo" : "dark"} minQty={minQty} />
					<Link to={href} onClick={() => rememberDiscoverySource(source)} className="line-clamp-1 text-[14px] leading-[14px] tracking-[-0.42px] text-ds-primary transition-colors hover:text-ds-accent">
						{product.name}
					</Link>
					{/* «В корзину» карточки подборки: DS Button gray S — 32px, r8, #989AA2 12 %, 600/12/16. */}
					<Button variant="gray" size="sm" block onClick={quickAdd} disabled={soldOut}>
						{soldOut ? t("product.outOfStock") : t("product.addToCart")}
					</Button>
				</div> : <div className="flex flex-col gap-0.5 px-1.5" data-testid="product-card-text">
					<Price value={tile?.price ?? product.price} oldValue={tile?.oldPrice} pill="dark" minQty={minQty} className="[&>span:first-child]:leading-5" />
					<div className="flex flex-col gap-0.5">
						{product.brand && <span className="text-[16px] leading-4 text-ds-primary">{product.brand}</span>}
						<Link to={href} onClick={() => rememberDiscoverySource(source)} className="line-clamp-1 text-[14px] leading-[18px] text-ds-muted transition-colors hover:text-ds-accent">
							{product.name}
						</Link>
					</div>
				</div>}
		</div>
  );
}